import { useEffect, useId, useMemo, useRef, useState, type ComponentType } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FileText as FileTextIcon,
  Loader as LoaderIcon,
  Plug as PlugIcon,
} from "lucide-react";
import type { WhatsAppConnectionResponse, WhatsAppWorkspacePhoneOption } from "@multi-chat/shared";
import { getWorkspacePhoneOptions } from "../../../api/whatsapp-api.js";
import { useWhatsAppConnection } from "../use-whatsapp-connection.js";
import { useMetaEmbeddedSignup } from "../use-meta-embedded-signup.js";
import { Button } from "../../../components/ui/button/index.js";
import { Badge } from "../../../components/ui/badge/index.js";
import { cn } from "../../../lib/utils.js";
import { LayoutGroup, motion, useReducedMotion } from "../../../lib/motion.js";
import { ConnectedDetails } from "../ConnectedDetails/ConnectedDetails.js";
import { MessageTemplatesSection } from "../MessageTemplatesSection/index.js";
import { PhoneManagementSheet } from "../PhoneManagementSheet/index.js";
import {
  AddPhoneWizard,
  signupPhoneRegistrationEntry,
  type AddPhoneWizardEntry,
} from "../add-phone/index.js";
import { useWhatsAppWorkspacePhone } from "../whatsapp-workspace-phone-context.js";
import { useChannelDetailHeader, useChannelDirectory } from "../../channels/ChannelsWorkspaceLayout.js";
import { useOrganizationPlatformFlags } from "../../organizations/use-organization-platform-flags.js";
import { buildWhatsAppCallingManagerUrl } from "../template-editor-presentation.js";
import {
  canLoadWhatsAppTemplates,
  shouldShowWhatsAppTemplatesConnectionRequired,
} from "../whatsapp-templates-access.js";
import { PhoneSelection } from "../PhoneSelection/PhoneSelection.js";
import { ChannelMark } from "../../../components/channel/ChannelMark.js";
import { AppNotice } from "../../../components/common/AppNotice.js";
import { ConfirmDisconnectDialog } from "../../../components/common/ConfirmDisconnectDialog.js";
import { WorkspaceLoader } from "../../../components/common/WorkspaceLoader.js";
import {
  WhatsAppAccountGate,
  WhatsAppConnectingPanel,
  WhatsAppConnectingScreen,
  WhatsAppTemplatesGate,
} from "../WhatsAppProviderStates.js";
import {
  pendingWhatsAppRegistrationMode,
  shouldShowWhatsAppConnecting,
  shouldShowWhatsAppPageSkeleton,
  type PendingWhatsAppRegistrationMode,
  type WhatsAppSetupIntent,
} from "../whatsapp-setup-presentation.js";
import { WhatsAppCancelPendingDialog } from "./WhatsAppCancelPendingDialog.js";
import { WhatsAppPendingResume } from "./WhatsAppPendingResume.js";
import {
  buildWhatsAppWorkspaceSearchParams,
  parseWhatsAppWorkspaceTab,
  shouldNormalizeWhatsAppWorkspaceTab,
  type WhatsAppWorkspaceTab,
} from "../whatsapp-workspace-tab-state.js";
import { WhatsAppWorkspaceTabPanel } from "../whatsapp-workspace-tabs.js";
import { MixedLocaleValue, TechnicalValue } from "../TechnicalValue.js";
import { whatsAppProviderTitle } from "../whatsapp-channel-title.js";
import {
  ChannelDetailFrame,
  ChannelDetailSkeleton,
  ConnectedChannelHeader,
} from "../../../components/channel/ConnectedChannelLayout.js";
import {
  claimWhatsAppSignupHandoff,
  WHATSAPP_SIGNUP_HANDOFF_EVENT,
} from "../whatsapp-signup-handoff.js";
import { OrganizationMessageBalanceBadge } from "../../organizations/OrganizationMessageBalanceBadge.js";

interface WhatsAppPageProps {
  organizationId: number;
  role: string;
}

const stateCardClass =
  "grid gap-4 overflow-hidden rounded-lg border border-line bg-surface px-5 py-5";
const stateCardIconClass =
  "inline-grid size-10 shrink-0 place-items-center rounded-md bg-whatsapp-muted text-whatsapp";
const actionsClass = "flex flex-wrap items-center gap-3";
const spinClass = "animate-[spin_0.8s_linear_infinite] motion-reduce:animate-none";
const tabListClass = "flex w-full max-w-full items-stretch gap-1 overflow-x-clip border-b border-line";
const tabClass =
  "relative inline-flex h-10 shrink-0 items-center gap-2 border-0 bg-transparent px-3 text-[0.875rem] font-medium text-ink-subtle transition-colors duration-150 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color-mix(in_srgb,var(--color-accent)_55%,transparent)]";
const tabActiveClass = "font-semibold text-ink";
const tabIconClass = "size-4 shrink-0";
const tabIndicatorClass =
  "pointer-events-none absolute start-3 end-3 -bottom-px h-0.5 rounded-full bg-whatsapp";
const tabIndicatorTransition = {
  duration: 0.2,
  ease: [0.25, 0.1, 0.25, 1] as const,
};
const workspaceStageClass =
  "min-w-0 pt-5 max-md:min-h-0 md:min-h-[calc(100dvh-12.5rem)]";
const whatsAppPageShellClass =
  "h-full min-h-0 overflow-y-auto px-4 py-5 text-start [scrollbar-gutter:stable] md:px-6 md:py-6";
const whatsAppHeaderColumnClass = "mx-auto w-full max-w-[40rem]";
const whatsAppTemplatesColumnClass = "mx-auto w-full max-w-[52rem]";

type PendingResumeState = {
  mode: PendingWhatsAppRegistrationMode;
  displayPhoneNumber: string | null;
  completeDisabled: boolean;
  onComplete: () => void;
};

type WorkspaceTabItem = {
  id: WhatsAppWorkspaceTab;
  label: string;
  tabId: string;
  Icon: ComponentType<{ className?: string; size?: number; "aria-hidden"?: boolean }>;
};

function stateToBadgeVariant(
  state: string,
): "default" | "success" | "warning" | "error" {
  switch (state) {
    case "CONNECTED":
      return "success";
    case "PENDING_PHONE_SELECTION":
      return "warning";
    case "ERROR":
      return "error";
    default:
      return "default";
  }
}

function stateLabel(t: (key: string) => string, state: string): string {
  switch (state) {
    case "NOT_CONNECTED":
      return t("whatsapp.notConnected.title");
    case "PENDING_PHONE_SELECTION":
      return t("whatsapp.pending.title");
    case "CONNECTED":
      return t("whatsapp.connected.title");
    case "DISCONNECTED":
      return t("whatsapp.disconnected.title");
    case "ERROR":
      return t("whatsapp.error.title");
    default:
      return state;
  }
}

export function WhatsAppPage({ organizationId, role }: WhatsAppPageProps): React.JSX.Element {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { applyConnection } = useWhatsAppWorkspacePhone();
  const directory = useChannelDirectory();
  const wizardHoldRef = useRef(false);
  const {
    view,
    refresh,
    refreshQuietly,
    applyPhoneProfilePicture,
    submitSignup,
    submitPhoneSelection,
    acceptConnection,
    switchWorkspacePhone,
    cancelPending,
    disconnect,
    busy,
    actionError,
    clearActionError,
  } = useWhatsAppConnection(organizationId, t("whatsapp.unreachable"), {
    onConnectionUpdated: applyConnection,
    holdConnectionView: () => wizardHoldRef.current,
  });

  const meta = useMetaEmbeddedSignup();
  const [setupIntent, setSetupIntent] = useState<WhatsAppSetupIntent>("idle");
  const [selectedPhoneId, setSelectedPhoneId] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [confirmCancelPending, setConfirmCancelPending] = useState(false);
  const [phoneSheetOpen, setPhoneSheetOpen] = useState(false);
  const [phoneSheetView, setPhoneSheetView] = useState<"home" | "wizard">("home");
  const [addPhoneEntry, setAddPhoneEntry] = useState<AddPhoneWizardEntry>({ kind: "new" });
  const [dismissedRegistrationId, setDismissedRegistrationId] = useState<string | null>(null);
  const [resumeWizardOpen, setResumeWizardOpen] = useState(false);
  const [resumeBlockedPhoneId, setResumeBlockedPhoneId] = useState<string | null>(null);
  const [workspacePhoneOptions, setWorkspacePhoneOptions] = useState<
    WhatsAppWorkspacePhoneOption[]
  >([]);
  const [workspacePhoneOptionsLoading, setWorkspacePhoneOptionsLoading] = useState(false);
  const [workspacePhoneOptionsError, setWorkspacePhoneOptionsError] = useState<string | null>(
    null,
  );
  const activeTab = parseWhatsAppWorkspaceTab(searchParams);
  const connectionTabId = useId();
  const templatesTabId = useId();
  const tabRefs = useRef<Record<WhatsAppWorkspaceTab, HTMLButtonElement | null>>({
    connection: null,
    templates: null,
  });

  const canManage = role === "OWNER" || role === "ADMIN";
  const platformFlags = useOrganizationPlatformFlags(organizationId);
  const whatsappDisabled = platformFlags != null && !platformFlags.whatsappEnabled;
  const tabs = useMemo(
    (): WorkspaceTabItem[] => [
      {
        id: "connection",
        label: t("whatsapp.workspace.tabs.connection"),
        tabId: connectionTabId,
        Icon: PlugIcon,
      },
      {
        id: "templates",
        label: t("whatsapp.workspace.tabs.templates"),
        tabId: templatesTabId,
        Icon: FileTextIcon,
      },
    ],
    [connectionTabId, t, templatesTabId],
  );

  useEffect(() => {
    setSelectedPhoneId(null);
    setConfirmDisconnect(false);
    setConfirmCancelPending(false);
    setDismissedRegistrationId(null);
    setResumeWizardOpen(false);
    setResumeBlockedPhoneId(null);
    clearActionError();
    meta.clearLaunchError();
  }, [organizationId, clearActionError, meta.clearLaunchError]);

  const connectableState =
    view.kind === "ready"
      ? view.connection.state === "NOT_CONNECTED" ||
        view.connection.state === "DISCONNECTED" ||
        view.connection.state === "ERROR"
      : false;

  useEffect(() => {
    if (!canManage || !connectableState) return;
    const controller = new AbortController();
    void meta.loadConfig(organizationId, controller.signal);
    return () => controller.abort();
  }, [organizationId, canManage, connectableState, meta.loadConfig]);

  useEffect(() => {
    if (!shouldNormalizeWhatsAppWorkspaceTab(searchParams)) return;
    setSearchParams(
      buildWhatsAppWorkspaceSearchParams(searchParams, activeTab),
      { replace: true },
    );
  }, [activeTab, searchParams, setSearchParams]);

  useEffect(() => {
    if (setupIntent === "idle") return;
    if (meta.launching || busy) return;
    setSetupIntent("idle");
  }, [setupIntent, meta.launching, busy]);

  const handleConnect = () => {
    if (!meta.config) return;
    meta.clearLaunchError();
    setSetupIntent("signup");
    meta.launch((result) => {
      void submitSignup(result).then(() => directory.reload());
    });
  };

  const organizationIdRef = useRef(organizationId);
  organizationIdRef.current = organizationId;

  useEffect(() => {
    const consumeHandoff = () => {
      const result = claimWhatsAppSignupHandoff(organizationIdRef.current);
      if (!result) return;
      setSetupIntent("signup");
      void submitSignup(result).then(() => directory.reload());
    };
    consumeHandoff();
    window.addEventListener(WHATSAPP_SIGNUP_HANDOFF_EVENT, consumeHandoff);
    return () => window.removeEventListener(WHATSAPP_SIGNUP_HANDOFF_EVENT, consumeHandoff);
  }, [directory.reload, organizationId, submitSignup]);

  const candidates =
    view.kind === "pending-selection" ? view.candidates.phoneNumberCandidates : [];
  const phoneRegistration =
    view.kind === "pending-selection" ? view.candidates.phoneRegistration : undefined;
  const storedRegistration =
    view.kind === "ready" && view.connection.state === "PENDING_PHONE_SELECTION"
      ? view.connection.phoneRegistration
      : undefined;
  const storedResume =
    view.kind === "ready" && view.connection.state === "PENDING_PHONE_SELECTION"
      ? view.connection.verificationResume
      : undefined;
  const pendingPhoneId =
    storedRegistration?.externalPhoneNumberId ?? phoneRegistration?.externalPhoneNumberId ?? null;
  const resumeBlocked = resumeBlockedPhoneId !== null && resumeBlockedPhoneId === pendingPhoneId;
  const registrationOpen =
    phoneRegistration != null && dismissedRegistrationId !== phoneRegistration.externalPhoneNumberId;
  const showResumeWizard =
    resumeWizardOpen && storedRegistration != null && !resumeBlocked;
  const activeRegistration = registrationOpen
    ? phoneRegistration
    : showResumeWizard
      ? storedRegistration
      : undefined;
  const wizardEntry = useMemo(() => {
    if (!activeRegistration) return null;
    return signupPhoneRegistrationEntry(
      activeRegistration,
      registrationOpen ? undefined : storedResume,
    );
  }, [activeRegistration, registrationOpen, storedResume]);
  wizardHoldRef.current = activeRegistration != null;
  const pendingResume: PendingResumeState | null = !canManage
    ? null
    : view.kind === "ready" && view.connection.state === "PENDING_PHONE_SELECTION"
      ? {
          mode: pendingWhatsAppRegistrationMode({
            externalPhoneNumberId: storedRegistration?.externalPhoneNumberId,
            resumeBlocked,
          }),
          displayPhoneNumber: storedRegistration?.displayPhoneNumber ?? null,
          completeDisabled: false,
          onComplete: () => setResumeWizardOpen(true),
        }
      : view.kind === "pending-selection" && phoneRegistration != null
        ? {
            mode: resumeBlocked ? "cancel-only" : "complete",
            displayPhoneNumber: phoneRegistration.displayPhoneNumber,
            completeDisabled: registrationOpen,
            onComplete: () => setDismissedRegistrationId(null),
          }
        : null;

  const togglePhone = (id: string) => {
    setSelectedPhoneId(id);
  };

  const handleSubmitSelection = () => {
    if (!selectedPhoneId || busy) return;
    setSetupIntent("phone");
    void submitPhoneSelection([selectedPhoneId]).then(() => directory.reload());
  };

  const handleCancelPendingRequest = () => {
    if (busy) return;
    setSelectedPhoneId(null);
    setResumeBlockedPhoneId(null);
    void cancelPending().then(() => directory.reload());
    setConfirmCancelPending(false);
  };

  const handleDisconnectRequest = () => {
    if (busy) return;
    void disconnect().then(() => directory.reload());
    setConfirmDisconnect(false);
  };

  const handleProfilePictureUpdated = async (input: {
    externalPhoneNumberId: string;
    profilePictureUrl: string | null;
  }) => {
    applyPhoneProfilePicture(input.externalPhoneNumberId, input.profilePictureUrl);
    await refreshQuietly();
  };

  useEffect(() => {
    if (!phoneSheetOpen) return;
    const controller = new AbortController();
    setWorkspacePhoneOptionsLoading(true);
    setWorkspacePhoneOptionsError(null);
    void getWorkspacePhoneOptions(organizationId, controller.signal)
      .then((response) => {
        setWorkspacePhoneOptions(response.options);
      })
      .catch(() => {
        setWorkspacePhoneOptions([]);
        setWorkspacePhoneOptionsError(t("whatsapp.switchWorkspacePhone.optionsError"));
      })
      .finally(() => {
        setWorkspacePhoneOptionsLoading(false);
      });
    return () => controller.abort();
  }, [phoneSheetOpen, organizationId, t]);

  const handleSwitchWorkspacePhone = async (externalPhoneNumberId: string): Promise<void> => {
    if (busy) return;
    await switchWorkspacePhone(externalPhoneNumberId);
  };

  const openPhoneManagement = (
    nextView: "home" | "wizard" = "home",
    entry: AddPhoneWizardEntry = { kind: "new" },
  ): void => {
    setPhoneSheetView(nextView);
    setAddPhoneEntry(entry);
    setPhoneSheetOpen(true);
  };

  const handleRegisterRequiredPhone = (option: WhatsAppWorkspacePhoneOption) => {
    openPhoneManagement("wizard", {
      kind: "existing",
      externalPhoneNumberId: option.externalPhoneNumberId,
      displayPhoneNumber: option.phoneNumber ?? option.displayPhoneNumber,
      verifiedName: option.verifiedName,
    });
  };

  const handleAddPhoneCompleted = (connection: WhatsAppConnectionResponse) => {
    applyConnection(connection);
  };

  const connectDisabled =
    whatsappDisabled ||
    busy ||
    meta.launching ||
    meta.configError !== null ||
    !meta.config ||
    !meta.config.enabled;

  const metaConfigErrorMessage = meta.configError === "FEATURE_DISABLED"
    ? t("whatsapp.integrationUnavailable")
    : meta.configError;

  const connecting = shouldShowWhatsAppConnecting({
    setupIntent,
    metaLaunching: meta.launching,
    busy,
  });

  const headerBadge = connecting ? (
    <Badge variant="warning">{t("whatsapp.connecting.badge")}</Badge>
  ) : view.kind === "ready" ? (
    <Badge variant={stateToBadgeVariant(view.connection.state)}>
      {stateLabel(t, view.connection.state)}
    </Badge>
  ) : null;
  const connectedAccount = view.kind === "ready" && view.connection.state === "CONNECTED"
    ? view.connection
    : null;
  const headerTitle = connectedAccount
    ? whatsAppProviderTitle(
        connectedAccount.workspacePhone?.displayName,
        t("channels.platforms.whatsapp"),
      )
    : t("whatsapp.title");
  const headerIdentity = connectedAccount?.workspacePhone?.phoneNumber ?? null;
  const showSignupFailure = view.kind === "error" || view.kind === "unavailable";
  const pageLoading = shouldShowWhatsAppPageSkeleton({
    connecting,
    viewKind: view.kind,
  });
  useChannelDetailHeader(
    pageLoading
      ? null
      : {
          title: connectedAccount ? headerTitle : t("channels.platforms.whatsapp"),
          disconnect:
            connectedAccount && canManage
              ? {
                  label: t("whatsapp.connected.disconnect"),
                  busy,
                  testId: "whatsapp-disconnect",
                  onClick: () => setConfirmDisconnect(true),
                }
              : null,
        },
  );

  function selectTab(tab: WhatsAppWorkspaceTab): void {
    setSearchParams(buildWhatsAppWorkspaceSearchParams(searchParams, tab), { replace: true });
  }

  function handleTabKeyDown(
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void {
    if (
      event.key !== "ArrowRight" &&
      event.key !== "ArrowLeft" &&
      event.key !== "Home" &&
      event.key !== "End"
    ) {
      return;
    }

    event.preventDefault();

    const tabCount = tabs.length;
    const isRtl = document?.documentElement.dir === "rtl";
    let nextIndex = index;

    if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = tabCount - 1;
    } else if (event.key === "ArrowRight") {
      nextIndex = (index + (isRtl ? -1 : 1) + tabCount) % tabCount;
    } else if (event.key === "ArrowLeft") {
      nextIndex = (index + (isRtl ? 1 : -1) + tabCount) % tabCount;
    }

    const nextTab = tabs[nextIndex];
    if (!nextTab) return;
    tabRefs.current[nextTab.id]?.focus();
    selectTab(nextTab.id);
  }

  const recoveryState = (
    <WhatsAppUnavailableState
      view={view}
      canManage={canManage}
      platformDisabled={whatsappDisabled}
      busy={busy}
      connectDisabled={connectDisabled}
      metaLaunching={meta.launching}
      metaConfigError={metaConfigErrorMessage}
      metaLaunchError={meta.launchError}
      connecting={connecting}
      actionError={actionError}
      selectedPhoneId={selectedPhoneId}
      onDismissActionError={clearActionError}
      onRetry={refresh}
      onConnect={handleConnect}
      onOpenCancelPending={() => setConfirmCancelPending(true)}
      onTogglePhone={togglePhone}
      onSubmitSelection={handleSubmitSelection}
      suppressPhoneSelection={phoneRegistration != null}
      pendingResume={pendingResume}
    />
  );

  return (
    <>
      {showSignupFailure ? (
        recoveryState
      ) : pageLoading ? (
        <ChannelDetailSkeleton
          label={t("whatsapp.loading")}
          testId="whatsapp-connection-loading"
          showTabs
        />
      ) : connectedAccount ? (
      <div className={whatsAppPageShellClass}>
        <div className={whatsAppHeaderColumnClass}>
        <WhatsAppWorkspaceHeader
          organizationId={organizationId}
          role={role}
          activeTab={activeTab}
          headerBadge={headerBadge}
          title={headerTitle}
          identity={headerIdentity}
          connected={connectedAccount != null}
          tabs={tabs}
          tabRefs={tabRefs}
          onSelectTab={selectTab}
          onTabKeyDown={handleTabKeyDown}
        />
        </div>

        <div className={activeTab === "connection" ? whatsAppHeaderColumnClass : whatsAppTemplatesColumnClass}>
        <div className={workspaceStageClass}>
          <WhatsAppWorkspaceTabPanel
            tab="connection"
            activeTab={activeTab}
            labelledBy={connectionTabId}
          >
            <ConnectionTabContent
              organizationId={organizationId}
              view={view}
              canManage={canManage}
              platformDisabled={whatsappDisabled}
              busy={busy}
              connectDisabled={connectDisabled}
              metaLaunching={meta.launching}
              metaConfigError={metaConfigErrorMessage}
              metaLaunchError={meta.launchError}
              connecting={connecting}
              actionError={actionError}
              selectedPhoneId={selectedPhoneId}
              onDismissActionError={clearActionError}
              onRetry={refresh}
              onConnect={handleConnect}
              onOpenCancelPending={() => setConfirmCancelPending(true)}
              onOpenSwitchWorkspacePhone={() => openPhoneManagement("home")}
              onOpenAddPhone={() => openPhoneManagement("wizard")}
              onProfilePictureUpdated={handleProfilePictureUpdated}
              onRetryProfilePictureRefresh={refreshQuietly}
              onTogglePhone={togglePhone}
              onSubmitSelection={handleSubmitSelection}
            />
          </WhatsAppWorkspaceTabPanel>

          <WhatsAppWorkspaceTabPanel
            tab="templates"
            activeTab={activeTab}
            labelledBy={templatesTabId}
          >
            {connecting ? (
              <WhatsAppConnectingPanel
                title={t("whatsapp.connecting.title")}
                description={t("whatsapp.connecting.message")}
              />
            ) : view.kind === "ready" && canLoadWhatsAppTemplates(view.connection.state) ? (
              <MessageTemplatesSection
                organizationId={organizationId}
                canManage={canManage && !whatsappDisabled}
                canSync={!whatsappDisabled}
                showHeading={false}
                callingManagerUrl={buildWhatsAppCallingManagerUrl({
                  metaBusinessId: view.connection.metaBusinessId,
                  phoneNumberId: view.connection.workspacePhone?.externalPhoneNumberId ??
                    view.connection.phoneNumbers[0]?.externalPhoneNumberId,
                })}
              />
            ) : view.kind === "ready" &&
              shouldShowWhatsAppTemplatesConnectionRequired(view.connection.state) ? (
              <WhatsAppTemplatesGate
                title={t("whatsapp.templates.connectRequiredTitle")}
                description={t("whatsapp.templates.connectRequired")}
                readonly={canManage ? null : t("whatsapp.readonly")}
                action={
                  canManage ? (
                    <Button variant="whatsapp" onClick={() => selectTab("connection")}>
                      {t("whatsapp.templates.goToConnection")}
                    </Button>
                  ) : null
                }
              />
            ) : (
              <WorkspaceLoader message={t("whatsapp.workspace.templatesUnavailable")} />
            )}
          </WhatsAppWorkspaceTabPanel>
        </div>
        </div>
      </div>
      ) : (
        recoveryState
      )}

      <ConfirmDisconnectDialog
        open={confirmDisconnect}
        onOpenChange={setConfirmDisconnect}
        platformType="WHATSAPP"
        title={t("whatsapp.disconnectConfirm.title")}
        description={t("whatsapp.disconnectConfirm.description")}
        cancelLabel={t("whatsapp.disconnectConfirm.cancel")}
        confirmLabel={t("whatsapp.disconnectConfirm.confirm")}
        pendingLabel={t("whatsapp.disconnectConfirm.disconnecting")}
        busy={busy}
        onConfirm={handleDisconnectRequest}
        testId="whatsapp-disconnect-dialog"
      />

      <WhatsAppCancelPendingDialog
        open={confirmCancelPending}
        onOpenChange={setConfirmCancelPending}
        busy={busy}
        onConfirm={handleCancelPendingRequest}
      />

      {activeRegistration && wizardEntry ? (
        <AddPhoneWizard
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setResumeWizardOpen(false);
              setDismissedRegistrationId(activeRegistration.externalPhoneNumberId);
            }
          }}
          organizationId={organizationId}
          entry={wizardEntry}
          sessionKey={activeRegistration.externalPhoneNumberId}
          unreachableMessage={t("whatsapp.unreachable")}
          onResumeUnavailable={() => {
            setResumeBlockedPhoneId(activeRegistration.externalPhoneNumberId);
            setResumeWizardOpen(false);
            setDismissedRegistrationId(activeRegistration.externalPhoneNumberId);
          }}
          onCompleted={(connection) => {
            setResumeWizardOpen(false);
            acceptConnection(connection);
            directory.reload();
          }}
        />
      ) : null}

      {view.kind === "ready" && view.connection.state === "CONNECTED" ? (
        <PhoneManagementSheet
          open={phoneSheetOpen}
          onOpenChange={setPhoneSheetOpen}
          organizationId={organizationId}
          connection={view.connection}
          options={workspacePhoneOptions}
          optionsLoading={workspacePhoneOptionsLoading}
          optionsError={workspacePhoneOptionsError}
          busy={busy}
          initialView={phoneSheetView}
          initialEntry={addPhoneEntry}
          unreachableMessage={t("whatsapp.unreachable")}
          onSwitch={handleSwitchWorkspacePhone}
          onCompleted={handleAddPhoneCompleted}
          onReloadOptions={() => {
            void getWorkspacePhoneOptions(organizationId).then((response) => {
              setWorkspacePhoneOptions(response.options);
            });
          }}
        />
      ) : null}
    </>
  );
}

function WhatsAppUnavailableState({
  view,
  canManage,
  platformDisabled,
  busy,
  connectDisabled,
  metaLaunching,
  metaConfigError,
  metaLaunchError,
  connecting,
  actionError,
  selectedPhoneId,
  onDismissActionError,
  onRetry,
  onConnect,
  onOpenCancelPending,
  onTogglePhone,
  onSubmitSelection,
  suppressPhoneSelection = false,
  pendingResume = null,
}: {
  view: ReturnType<typeof useWhatsAppConnection>["view"];
  canManage: boolean;
  platformDisabled: boolean;
  busy: boolean;
  connectDisabled: boolean;
  metaLaunching: boolean;
  metaConfigError: string | null;
  metaLaunchError: string | null;
  connecting: boolean;
  actionError: string | null;
  selectedPhoneId: string | null;
  onDismissActionError: () => void;
  onRetry: () => void;
  onConnect: () => void;
  onOpenCancelPending: () => void;
  onTogglePhone: (id: string) => void;
  onSubmitSelection: () => void;
  suppressPhoneSelection?: boolean;
  pendingResume?: PendingResumeState | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  if (connecting) {
    return (
      <WhatsAppConnectingScreen
        title={t("whatsapp.connecting.title")}
        description={t("whatsapp.connecting.message")}
        note={t("whatsapp.connecting.secureNote")}
      />
    );
  }
  const connectionState = view.kind === "ready"
    ? view.connection.state
    : view.kind === "pending-selection"
      ? "PENDING_PHONE_SELECTION"
      : "NOT_CONNECTED";
  const description =
    view.kind === "ready" &&
    !(connectionState === "PENDING_PHONE_SELECTION" && pendingResume)
      ? whatsAppUnavailableDescription(t, view.connection.state)
      : null;
  const notice = platformDisabled
    ? t("channels.platformDisabled")
    : (metaConfigError ?? metaLaunchError);
  const showConnect =
    view.kind === "ready" &&
    (connectionState === "NOT_CONNECTED" ||
      connectionState === "DISCONNECTED" ||
      connectionState === "ERROR") &&
    canManage &&
    !platformDisabled &&
    !metaConfigError;
  const gateTestId =
    connectionState === "DISCONNECTED"
      ? "whatsapp-disconnected"
      : connectionState === "ERROR"
        ? "whatsapp-connection-error-state"
        : connectionState === "NOT_CONNECTED"
          ? "whatsapp-not-connected"
          : "whatsapp-unavailable";

  return (
    <ChannelDetailFrame
      testId="channels-whatsapp-unavailable"
      platformType="WHATSAPP"
      title={t("channels.platforms.whatsapp")}
      state={connectionState}
      error={view.kind === "error" || view.kind === "unavailable" ? view.message : null}
    >
      {actionError ? (
        <AppNotice
          severity="error"
          description={actionError}
          onDismiss={onDismissActionError}
          dismissLabel={t("whatsapp.dismiss")}
          testId="whatsapp-action-error"
        />
      ) : null}
      {description ? (
        <p className="m-0 max-w-md text-[0.8125rem] text-muted" data-testid={gateTestId}>
          {description}
        </p>
      ) : null}
      {notice ? (
        <AppNotice
          severity="error"
          description={notice}
          testId="whatsapp-unavailable-notice"
        />
      ) : null}
      {!canManage && showConnect === false && view.kind === "ready" &&
      (connectionState === "NOT_CONNECTED" ||
        connectionState === "DISCONNECTED" ||
        connectionState === "ERROR") ? (
        <p className="m-0 text-[0.8125rem] text-muted">{t("whatsapp.readonly")}</p>
      ) : null}
      {showConnect ? (
        <Button
          type="button"
          variant="whatsapp"
          onClick={onConnect}
          disabled={connectDisabled}
          data-testid="whatsapp-connect"
        >
          {metaLaunching ? t("whatsapp.openingMeta") : whatsAppConnectActionLabel(t, connectionState)}
        </Button>
      ) : null}
      {view.kind === "error" || view.kind === "unavailable" ? (
        <Button type="button" size="sm" variant="outline" onClick={onRetry}>
          {t("whatsapp.retry")}
        </Button>
      ) : null}
      {view.kind === "pending-selection" && !suppressPhoneSelection ? (
        <div className="w-full text-start">
          <PhoneSelection
            candidates={view.candidates.phoneNumberCandidates}
            selectedId={selectedPhoneId}
            onSelect={onTogglePhone}
            onSubmit={onSubmitSelection}
            onCancel={onOpenCancelPending}
            busy={busy}
            submitDisabled={platformDisabled}
          />
        </div>
      ) : null}
      {pendingResume ? (
        <WhatsAppPendingResume
          mode={pendingResume.mode}
          displayPhoneNumber={pendingResume.displayPhoneNumber}
          busy={busy}
          disabled={platformDisabled || pendingResume.completeDisabled}
          onComplete={pendingResume.onComplete}
          onCancel={onOpenCancelPending}
        />
      ) : null}
    </ChannelDetailFrame>
  );
}

function whatsAppUnavailableDescription(
  t: (key: string) => string,
  state: WhatsAppConnectionResponse["state"],
): string | null {
  switch (state) {
    case "NOT_CONNECTED":
      return t("whatsapp.notConnected.message");
    case "DISCONNECTED":
      return t("whatsapp.disconnected.message");
    case "ERROR":
      return t("whatsapp.error.message");
    case "PENDING_PHONE_SELECTION":
      return t("whatsapp.pending.message");
    case "CONNECTED":
      return null;
    default: {
      const unreachable: never = state;
      return unreachable;
    }
  }
}

function whatsAppConnectActionLabel(
  t: (key: string) => string,
  state: WhatsAppConnectionResponse["state"],
): string {
  switch (state) {
    case "DISCONNECTED":
      return t("whatsapp.disconnected.reconnect");
    case "ERROR":
      return t("whatsapp.error.reconnect");
    case "NOT_CONNECTED":
    case "PENDING_PHONE_SELECTION":
    case "CONNECTED":
      return t("whatsapp.notConnected.connect");
    default: {
      const unreachable: never = state;
      return unreachable;
    }
  }
}

function WhatsAppWorkspaceHeader({
  organizationId,
  role,
  activeTab,
  headerBadge,
  title,
  identity,
  connected,
  tabs,
  tabRefs,
  onSelectTab,
  onTabKeyDown,
}: {
  organizationId: number;
  role: string;
  activeTab: WhatsAppWorkspaceTab;
  headerBadge: React.JSX.Element | null;
  title: string;
  identity: string | null;
  connected: boolean;
  tabs: readonly WorkspaceTabItem[];
  tabRefs: React.RefObject<Record<WhatsAppWorkspaceTab, HTMLButtonElement | null>>;
  onSelectTab: (tab: WhatsAppWorkspaceTab) => void;
  onTabKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  return (
    <header className="shrink-0">
      {connected ? (
        <div className="pb-4">
          <ConnectedChannelHeader
            platformType="WHATSAPP"
            title={<MixedLocaleValue className="wrap-anywhere">{title}</MixedLocaleValue>}
            badge={
              <>
                {headerBadge}
                <OrganizationMessageBalanceBadge organizationId={organizationId} role={role} />
              </>
            }
            identity={
              identity ? (
                <TechnicalValue className="font-medium tabular-nums wrap-anywhere">
                  {identity}
                </TechnicalValue>
              ) : (
                t("whatsapp.workspace.connectionDescription")
              )
            }
          />
        </div>
      ) : (
        <div className="grid justify-items-center gap-2 pb-4 text-center">
          <ChannelMark platformType="WHATSAPP" size="hero" />
          <div className="grid justify-items-center gap-1">
            <div className="flex min-w-0 flex-wrap items-center justify-center gap-2">
              <h1 className="m-0 text-[1.25rem] font-semibold tracking-[-0.01em] text-foreground">
                {title}
              </h1>
              {headerBadge}
              <OrganizationMessageBalanceBadge organizationId={organizationId} role={role} />
            </div>
            <p className="m-0 max-w-md text-[0.8125rem] leading-snug text-muted">
              {t("whatsapp.workspace.description")}
            </p>
          </div>
        </div>
      )}

      <LayoutGroup>
        <div
          className={tabListClass}
          role="tablist"
          aria-label={t("whatsapp.workspace.tabsLabel")}
        >
          {tabs.map((tab, index) => {
            const selected = activeTab === tab.id;
            const Icon = tab.Icon;
            return (
              <button
                key={tab.id}
                ref={(node) => {
                  tabRefs.current[tab.id] = node;
                }}
                id={tab.tabId}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-controls={`whatsapp-tabpanel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                className={cn(tabClass, selected && tabActiveClass)}
                onClick={() => onSelectTab(tab.id)}
                onKeyDown={(event) => onTabKeyDown(event, index)}
              >
                <Icon
                  className={cn(tabIconClass, selected ? "text-whatsapp" : "text-current")}
                  aria-hidden={true}
                />
                <span className="whitespace-nowrap">{tab.label}</span>
                {selected ? (
                  <motion.span
                    layoutId="whatsapp-workspace-active-tab"
                    className={tabIndicatorClass}
                    transition={reduceMotion ? { duration: 0 } : tabIndicatorTransition}
                    aria-hidden="true"
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      </LayoutGroup>
    </header>
  );
}

function ConnectionTabContent({
  organizationId,
  view,
  canManage,
  platformDisabled,
  busy,
  connectDisabled,
  metaLaunching,
  metaConfigError,
  metaLaunchError,
  connecting,
  actionError,
  selectedPhoneId,
  onDismissActionError,
  onRetry,
  onConnect,
  onOpenCancelPending,
  onOpenSwitchWorkspacePhone,
  onOpenAddPhone,
  onProfilePictureUpdated,
  onRetryProfilePictureRefresh,
  onTogglePhone,
  onSubmitSelection,
}: {
  organizationId: number;
  view: ReturnType<typeof useWhatsAppConnection>["view"];
  canManage: boolean;
  platformDisabled: boolean;
  busy: boolean;
  connectDisabled: boolean;
  metaLaunching: boolean;
  metaConfigError: string | null;
  metaLaunchError: string | null;
  actionError: string | null;
  selectedPhoneId: string | null;
  onDismissActionError: () => void;
  onRetry: () => void;
  onConnect: () => void;
  onOpenCancelPending: () => void;
  onOpenSwitchWorkspacePhone: () => void;
  onOpenAddPhone: () => void;
  onProfilePictureUpdated: (input: {
    externalPhoneNumberId: string;
    profilePictureUrl: string | null;
  }) => Promise<void>;
  onRetryProfilePictureRefresh: () => Promise<void>;
  onTogglePhone: (id: string) => void;
  onSubmitSelection: () => void;
  connecting: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <section className="grid gap-4">
      {actionError ? (
        <AppNotice
          severity="error"
          description={actionError}
          onDismiss={onDismissActionError}
          dismissLabel={t("whatsapp.dismiss")}
          testId="whatsapp-action-error"
        />
      ) : null}

      {connecting ? (
        <WhatsAppConnectingPanel
          title={t("whatsapp.connecting.title")}
          description={t("whatsapp.connecting.message")}
        />
      ) : null}

      {!connecting && view.kind === "unavailable" ? (
        <AppNotice
          severity="error"
          description={view.message}
          testId="whatsapp-connection-unavailable"
          action={
            <Button type="button" size="sm" variant="outline" onClick={onRetry}>
              {t("whatsapp.retry")}
            </Button>
          }
        />
      ) : null}

      {!connecting && view.kind === "error" ? (
        <AppNotice
          severity="error"
          title={view.message}
          description={view.code ? t("whatsapp.code", { code: view.code }) : undefined}
          testId="whatsapp-connection-error"
          action={
            <Button type="button" size="sm" variant="outline" onClick={onRetry}>
              {t("whatsapp.retry")}
            </Button>
          }
        />
      ) : null}

      {!connecting && view.kind === "ready" ? (
        <ConnectionState
          organizationId={organizationId}
          connection={view.connection}
          canManage={canManage}
          busy={busy}
          connectDisabled={connectDisabled}
          metaLaunching={metaLaunching}
          metaConfigError={metaConfigError}
          metaLaunchError={metaLaunchError}
          onConnect={onConnect}
          onCancelPending={onOpenCancelPending}
          onOpenSwitchWorkspacePhone={onOpenSwitchWorkspacePhone}
          onOpenAddPhone={onOpenAddPhone}
          onProfilePictureUpdated={onProfilePictureUpdated}
          onRetryProfilePictureRefresh={onRetryProfilePictureRefresh}
          platformDisabled={platformDisabled}
        />
      ) : null}

      {!connecting && view.kind === "pending-selection" ? (
        <PhoneSelection
          candidates={view.candidates.phoneNumberCandidates}
          selectedId={selectedPhoneId}
          onSelect={onTogglePhone}
          onSubmit={onSubmitSelection}
          onCancel={onOpenCancelPending}
          busy={busy}
          submitDisabled={platformDisabled}
        />
      ) : null}
    </section>
  );
}

interface ConnectionStateProps {
  organizationId: number;
  connection: WhatsAppConnectionResponse;
  canManage: boolean;
  busy: boolean;
  connectDisabled: boolean;
  metaLaunching: boolean;
  metaConfigError: string | null;
  metaLaunchError: string | null;
  onConnect: () => void;
  onCancelPending: () => void;
  onOpenSwitchWorkspacePhone: () => void;
  onOpenAddPhone: () => void;
  onProfilePictureUpdated: (input: {
    externalPhoneNumberId: string;
    profilePictureUrl: string | null;
  }) => Promise<void>;
  onRetryProfilePictureRefresh: () => Promise<void>;
  platformDisabled: boolean;
}

function ConnectionState(props: ConnectionStateProps): React.JSX.Element | null {
  const { t } = useTranslation();
  const {
    organizationId,
    connection,
    canManage,
    busy,
    connectDisabled,
    metaLaunching,
    metaConfigError,
    metaLaunchError,
    onConnect,
    onCancelPending,
    onOpenSwitchWorkspacePhone,
    onOpenAddPhone,
    onProfilePictureUpdated,
    onRetryProfilePictureRefresh,
    platformDisabled,
  } = props;

  const benefits = [
    t("whatsapp.notConnected.benefit1"),
    t("whatsapp.notConnected.benefit2"),
    t("whatsapp.notConnected.benefit3"),
  ];

  switch (connection.state) {
    case "NOT_CONNECTED":
      return (
        <WhatsAppAccountGate
          testId="whatsapp-not-connected"
          title={t("whatsapp.notConnected.title")}
          description={t("whatsapp.notConnected.message")}
          benefits={canManage && !metaConfigError ? benefits : undefined}
          readonly={canManage ? null : t("whatsapp.readonly")}
          notice={
            platformDisabled
              ? t("channels.platformDisabled")
              : (metaConfigError ?? metaLaunchError)
          }
          action={
            canManage && !metaConfigError ? (
              <Button variant="whatsapp" onClick={onConnect} disabled={connectDisabled}>
                {metaLaunching ? t("whatsapp.openingMeta") : t("whatsapp.notConnected.connect")}
              </Button>
            ) : null
          }
        />
      );

    case "PENDING_PHONE_SELECTION":
      return (
        <div className={stateCardClass}>
          <span className={stateCardIconClass} aria-hidden="true">
            <LoaderIcon size={20} className={spinClass} />
          </span>
          <div>
            <h3 className="m-0 text-[1.05rem] font-semibold tracking-[-0.01em]">{t("whatsapp.pending.title")}</h3>
            <p className="m-0 leading-[1.6] text-muted">{t("whatsapp.pending.message")}</p>
          </div>
          {canManage ? (
            <div className={actionsClass}>
              <Button variant="link" linkDestructive onClick={onCancelPending} disabled={busy}>
                {t("whatsapp.pending.cancel")}
              </Button>
            </div>
          ) : null}
        </div>
      );

    case "CONNECTED":
      return (
        <div className="grid gap-4">
          <ConnectedDetails
            organizationId={organizationId}
            connection={connection}
            canSwitchWorkspacePhone={
              canManage &&
              !platformDisabled &&
              connection.permissions.canSwitchWorkspacePhone
            }
            canAddPhoneNumber={
              canManage && !platformDisabled && connection.permissions.canAddPhoneNumber
            }
            canUpdateBusinessProfile={
              canManage &&
              !platformDisabled &&
              (connection.permissions.canUpdateBusinessProfile ?? true)
            }
            onSwitchWorkspacePhone={onOpenSwitchWorkspacePhone}
            onAddPhoneNumber={onOpenAddPhone}
            onProfilePictureUpdated={onProfilePictureUpdated}
            onRetryProfilePictureRefresh={onRetryProfilePictureRefresh}
            switchBusy={busy}
          />
        </div>
      );

    case "DISCONNECTED":
      return (
        <WhatsAppAccountGate
          testId="whatsapp-disconnected"
          title={t("whatsapp.disconnected.title")}
          description={t("whatsapp.disconnected.message")}
          readonly={canManage ? null : t("whatsapp.readonly")}
          notice={
            platformDisabled
              ? t("channels.platformDisabled")
              : (metaConfigError ?? metaLaunchError)
          }
          action={
            canManage && !metaConfigError ? (
              <Button variant="whatsapp" onClick={onConnect} disabled={connectDisabled}>
                {metaLaunching ? t("whatsapp.openingMeta") : t("whatsapp.disconnected.reconnect")}
              </Button>
            ) : null
          }
        />
      );

    case "ERROR":
      return (
        <WhatsAppAccountGate
          testId="whatsapp-connection-error-state"
          title={t("whatsapp.error.title")}
          description={t("whatsapp.error.message")}
          notice={
            platformDisabled
              ? t("channels.platformDisabled")
              : (metaConfigError ?? metaLaunchError)
          }
          action={
            canManage && !metaConfigError ? (
              <Button variant="whatsapp" onClick={onConnect} disabled={connectDisabled}>
                {metaLaunching ? t("whatsapp.openingMeta") : t("whatsapp.error.reconnect")}
              </Button>
            ) : null
          }
        />
      );

    default:
      return null;
  }
}

